import React, { useEffect } from 'react'
import { KeyboardAvoidingView, ScrollView } from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { B1, H5, Icon, Stack } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { i18n } from '@zet/localization'
import { useForm } from 'react-hook-form'

import { useApiAction } from '../../api/useApiAction'
import { Field, GroupedFields } from '../../types'
import { convertFormDataToPayload } from '../../utils/convertFormDataToPayload'
import { setFormData } from '../../utils/setFormValues'
import AccordianComponent from '../common/accordianComponent'
import Footer from '../footer/footer'
import { ajvResolver } from '../formBuilder/resolver'

const LoanReferences = (_props: any) => {
  const { state, props } = _props
  const { data, error, isLoading, stateManagePost } = useApiAction()
  const ocFields = data?.layout?.ocFields
  const fieldValues = data?.layout?.fieldValues
  const step = data?.layout?.step || data?.metadata?.step
  const title = data?.layout?.title
  const ocFieldErrors = data?.error?.errorFieldIds ?? error?.formErrors
  const validationSchema = data?.layout?.validationSchema || {}
  const prevState = data?.prevState || ''

  const subTitle = data?.layout?.description?.replace(
    /{(.*?)}/g,
    (_: string, key: string) => data?.metadata[key] || `{${key}}`
  )
  const { control, handleSubmit, setError, setValue } = useForm({
    resolver: ajvResolver(validationSchema),
    mode: 'onTouched',
  })

  const onSubmit = (data) => {
    stateManagePost({ payload: convertFormDataToPayload(data) })
  }
  const setFormErrors = () => {
    ocFieldErrors?.forEach?.((error) => {
      if (error?.code) {
        setError(String(error.code), {
          type: 'server',
          message: error?.message ?? 'This field is invalid',
        })
      } else {
        setError(error, {
          type: 'server',
          message: 'This field is invalid',
        })
      }
    })
  }
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (ocFields?.length) {
      setFormData(fieldValues, setValue)
      if (ocFieldErrors) {
        timeout = setTimeout(() => {
          setFormErrors()
        })
      }
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [fieldValues])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (ocFieldErrors && !isLoading) {
      timeout = setTimeout(() => {
        setFormErrors()
      })
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [ocFieldErrors, isLoading])

  function groupFieldsBySuffix(fields: Field[] = []): GroupedFields {
    const grouped: GroupedFields = {}

    function removeDuplicates(fieldsArray: Field[] = []): Field[] {
      const uniqueFields = new Map<string, Field>()
      fieldsArray.forEach((field) => {
        if (field.ocFieldCode) {
          uniqueFields.set(field.ocFieldCode, field)
        }
      })
      return Array.from(uniqueFields.values())
    }

    const sortedFields = [...fields].sort(
      (a, b) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0)
    )

    sortedFields.forEach((field, index) => {
      if (!field.code) {
        console.warn(`Field with id ${field.id} is missing a code.`)
        return
      }

      const suffix = field.code.includes('_') ? field.code.split('_')[1] : '1'

      if (!grouped[suffix]) {
        grouped[suffix] = []
      }

      if (field.type === 'pincode' && index > 0) {
        const prevField = sortedFields[index - 1]
        const prevSuffix = prevField.code.includes('_')
          ? prevField.code.split('_')[1]
          : '1'
        if (grouped[prevSuffix]) {
          grouped[prevSuffix].push({ ...field })
        }
      } else {
        grouped[suffix].push({ ...field })
      }
    })

    Object.keys(grouped).forEach((key) => {
      grouped[key] = removeDuplicates(grouped[key])
    })
    return grouped
  }
  const groupedFields = groupFieldsBySuffix(ocFields)

  const navigation = useNavigation()
  const { params } = useRoute()
  const { hash } = params || {}

  const goToPrevState = () => {
    navigation.replace('BrandsCC', {
      state: prevState,
      hash,
    })
  }

  return (
    <KeyboardAvoidingView style={{ flex: 1 }}>
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, backgroundColor: 'white' }}
        keyboardShouldPersistTaps="handled"
      >
        <Stack style={{ flex: 1 }}>
          {prevState ? (
            <Stack className="m-3 items-start">
              <Pressable
                className="rounded bg-[#F5F7FB] p-1"
                onPress={goToPrevState}
              >
                <Icon type="ArrowLeft" color="#2F3440" />
              </Pressable>
            </Stack>
          ) : null}
          {!!title && (
            <H5 className={`${prevState ? '' : 'mt-4'} px-3`}>{title}</H5>
          )}
          {!!subTitle && <B1 className="text-grays-70 px-3">{subTitle}</B1>}
          {Object.entries(groupedFields).map(([prefix, fields], index) => (
            <AccordianComponent
              control={control}
              key={prefix}
              title={i18n.t(
                `loanApplications.loanReferences.addReference${index + 1}`
              )}
              props={props}
              ocFields={fields}
            />
          ))}
        </Stack>
      </ScrollView>

      <Footer
        isForm
        control={control}
        buttonProps={{
          loading: isLoading,
          onPress: handleSubmit(onSubmit),
        }}
        step={step}
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}
      />
    </KeyboardAvoidingView>
  )
}
export default LoanReferences
